import { isDeviceOnboardingTask } from "shared-core/types/device-onboarding.ts";
import type { DeviceTaskOperatingSystem } from "shared-core/types/operating-system.ts";
import {
  isDeviceTask,
  type Task,
  TaskAutomationStatus,
  TaskId,
  TaskLabel,
} from "shared-core/types/task";
import { detectBrowserDesktopOs } from "shared-core/utils/browser-os.ts";
import type { TranslateFn } from "shared-core/utils/task-translation.ts";
import microsoftLogo from "shared-ui/assets/images/microsoft.svg";
import androidLogo from "shared-ui/assets/os-logos/android.svg";
import appleLogo from "shared-ui/assets/os-logos/apple-logo.svg";
import linuxLogo from "shared-ui/assets/os-logos/linux.svg";
import windowsLogo from "shared-ui/assets/os-logos/windows-black.svg";
import googleWorkspaceLogo from "shared-ui/assets/system-logos/google_workspace.svg";
import metaBusinessSuiteLogo from "shared-ui/assets/system-logos/meta_business_suite.svg";
import microsoft365Logo from "shared-ui/assets/system-logos/microsoft_365.svg";
import wordpressLogo from "shared-ui/assets/system-logos/wordpress.svg";

/**
 * Wizard tasks that still belong in the ordinary task card. The illustrated
 * wizard card is for the onboarding flows; a task that merely happens to open
 * full-screen is a normal task as far as any list is concerned.
 */
const STANDARD_CARD_WIZARD_TASKS = new Set<TaskId>([
  TaskId.SPOT_THE_PHISH,
  TaskId.SPEAK_BINARY,
]);

/**
 * Which of the two cards a task is drawn with. Independent of how it opens —
 * `task.type` still decides between the dialog and a full-page route.
 */
export function usesWizardCard(task: Pick<Task, "id" | "type">): boolean {
  return task.type === "wizard" && !STANDARD_CARD_WIZARD_TASKS.has(task.id);
}

export const LABEL_TO_ICON: Partial<Record<TaskLabel, string>> = {
  [TaskLabel.APPLE_ID]: appleLogo,
  [TaskLabel.APPLE_ACCOUNT]: appleLogo,
  [TaskLabel.MAC]: appleLogo,
  [TaskLabel.IOS]: appleLogo,
  [TaskLabel.ANDROID]: androidLogo,
  [TaskLabel.GWS]: googleWorkspaceLogo,
  [TaskLabel.MICROSOFT_365]: microsoft365Logo,
  [TaskLabel.OUTLOOK]: microsoftLogo,
  [TaskLabel.WINDOWS]: windowsLogo,
  [TaskLabel.LINUX]: linuxLogo,
  [TaskLabel.WORDPRESS]: wordpressLogo,
  [TaskLabel.SOCIAL_MEDIA]: metaBusinessSuiteLogo,
};

/** OS-specific labels that should be narrowed to the current OS. */
const OS_LABELS = new Set<TaskLabel>([
  TaskLabel.MAC,
  TaskLabel.WINDOWS,
  TaskLabel.LINUX,
  TaskLabel.IOS,
  TaskLabel.ANDROID,
]);

/**
 * Get the platform icon path for a task based on its labels.
 * Returns the first matching icon or null if no platform label found.
 */
function getPlatformIcon(labels: TaskLabel[]): string | null {
  for (const label of labels) {
    const icon = LABEL_TO_ICON[label];
    if (icon) return icon;
  }
  return null;
}

/** Labels that represent a platform (have a display icon + text). */
export const PLATFORM_LABELS = new Set<TaskLabel>([
  TaskLabel.APPLE_ID,
  TaskLabel.APPLE_ACCOUNT,
  TaskLabel.MAC,
  TaskLabel.GWS,
  TaskLabel.MICROSOFT_365,
  TaskLabel.OUTLOOK,
  TaskLabel.WINDOWS,
  TaskLabel.LINUX,
  TaskLabel.IOS,
  TaskLabel.ANDROID,
  TaskLabel.WORDPRESS,
  TaskLabel.SOCIAL_MEDIA,
]);

/** Narrow platform labels to the current OS when known. */
function getPlatformLabels(
  labels: TaskLabel[],
  currentOS?: TaskLabel | null,
): TaskLabel[] {
  const platformLabels = labels.filter((label) => PLATFORM_LABELS.has(label));
  if (currentOS && platformLabels.includes(currentOS)) {
    return platformLabels.filter(
      (label) => !OS_LABELS.has(label) || label === currentOS,
    );
  }
  return platformLabels;
}

/**
 * Get a platform breadcrumb (icon + translated display text) for a task.
 * When `currentOS` is provided and the task has multiple OS labels,
 * only the matching OS label is kept so the icon reflects the user's actual OS.
 * @example getPlatformLabel([MAC, WINDOWS, LINUX], TaskLabel.WINDOWS) → { icon: "…windows-black.svg", text: "Windows" }
 * @example getPlatformLabel([APPLE_ID, MAC]) → { icon: "…apple-logo.svg", text: "Apple ID · macOS" }
 */
export function getPlatformLabel(
  labels: TaskLabel[],
  t: TranslateFn,
  currentOS?: TaskLabel | null,
): { icon: string; text: string } | null {
  const platformLabels = getPlatformLabels(labels, currentOS);
  const icon = getPlatformIcon(platformLabels);
  if (!icon) return null;

  const text = platformLabels
    .map((label) => t(`shared_ui.task_labels.${label}`))
    .join("・");
  return { icon, text };
}

export function getTaskPlatformLabel(
  task: Task,
  t: TranslateFn,
): { icon: string; text: string } | null {
  const assignedDeviceOS = isDeviceTask(task) ? task.deviceOs : null;
  const osLabel = getClientOSLabel(assignedDeviceOS);
  const platformLabel = getPlatformLabel(task.labels, t, osLabel);
  if (platformLabel || assignedDeviceOS === null || osLabel === null) {
    return platformLabel;
  }
  return getPlatformLabel([osLabel], t);
}

export function getTaskPlatformIcon(
  labels: TaskLabel[],
  osHint: TaskLabel | null,
): string | null {
  const icon = getPlatformIcon(getPlatformLabels(labels, osHint));
  if (icon) return icon;
  return osHint ? getPlatformIcon([osHint]) : null;
}

export function findUserTaskByOpenQuery(
  userTasks: readonly Task[],
  query: { open?: unknown; device?: unknown; action?: unknown },
  options: {
    completed: boolean;
    /** The app's own device, null while it resolves; omit on web, which has none. */
    currentDeviceInventoryId?: string | null | undefined;
  },
): Task | undefined {
  const targetActionId =
    typeof query.action === "string" ? Number(query.action) : null;
  if (targetActionId != null && Number.isInteger(targetActionId)) {
    return userTasks.find(
      (task) =>
        task.taskId === targetActionId &&
        task.isCompleted === options.completed,
    );
  }

  if (typeof query.open !== "string") return undefined;
  const fixReference = query.open;
  const matches = userTasks.filter(
    (task) =>
      task.fixReference === fixReference &&
      task.isCompletedOrSkipped === options.completed,
  );

  const targetDeviceInventoryId =
    typeof query.device === "string" ? query.device : null;
  if (targetDeviceInventoryId !== null) {
    return matches.find(
      (task) => taskVisibleDeviceInventoryId(task) === targetDeviceInventoryId,
    );
  }
  if (options.currentDeviceInventoryId === undefined) return matches[0];

  const onThisDevice = matches.filter((task) => {
    const deviceInventoryId = taskVisibleDeviceInventoryId(task);
    return (
      deviceInventoryId == null ||
      deviceInventoryId === options.currentDeviceInventoryId
    );
  });
  if (onThisDevice.length > 0) return onThisDevice[0];
  // An unresolved id still opens a lone candidate; between siblings it can't guess.
  return options.currentDeviceInventoryId === null && matches.length === 1
    ? matches[0]
    : undefined;
}

/** The device task of the app's own device, null while its id resolves. */
export function findCurrentDeviceTask(
  deviceTasks: readonly Task[],
  currentDeviceInventoryId: string | null,
): Task | undefined {
  // An unresolved id still gets a lone device; between siblings it can't guess.
  const deviceInventoryId =
    currentDeviceInventoryId ??
    (hasMultipleTaskDevices(deviceTasks)
      ? null
      : deviceTasks[0] && taskVisibleDeviceInventoryId(deviceTasks[0]));
  if (deviceInventoryId == null) return undefined;
  const onDevice = deviceTasks.filter(
    (task) => taskVisibleDeviceInventoryId(task) === deviceInventoryId,
  );
  // A reopened device task keeps its completed row as history; the open one is current.
  return onDevice.find((task) => !task.isCompleted) ?? onDevice[0];
}

const AGENT_OS_TO_LABEL: Record<string, TaskLabel> = {
  macos: TaskLabel.MAC,
  windows: TaskLabel.WINDOWS,
  linux: TaskLabel.LINUX,
  ios: TaskLabel.IOS,
  android: TaskLabel.ANDROID,
};

/**
 * Detect the client OS and return the matching TaskLabel.
 * Uses the agent OS string when available, otherwise parses the browser UA.
 */
export function getClientOSLabel(agentOS: string | null): TaskLabel | null {
  if (agentOS) {
    return AGENT_OS_TO_LABEL[agentOS.toLowerCase()] ?? null;
  }
  const browserOs = detectBrowserDesktopOs();
  return browserOs ? (AGENT_OS_TO_LABEL[browserOs] ?? null) : null;
}

export interface TaskVideo {
  videoId: string;
  duration?: string | undefined;
}

/**
 * Static map from fixReference → video data.
 * Add entries here when a task has an instructional video.
 */
const VIDEO_MAP: Record<string, TaskVideo> = {
  // example_task_reference: { videoId: "as-9me-Gj-o", duration: "01:23" },
};

export function getTaskVideo(fixReference: string): TaskVideo | null {
  return VIDEO_MAP[fixReference] ?? null;
}

/** Maximum number of tags to display in the task dialog. */
export const MAX_DISPLAY_TAGS = 4;

/** Labels that should never appear as user-facing tags. */
const HIDDEN_LABELS = new Set<TaskLabel>([
  TaskLabel.LOW_PRIORITY,
  TaskLabel.MEDIUM_PRIORITY,
  TaskLabel.HIGH_PRIORITY,
  TaskLabel.INFO_RISK_LEVEL,
  TaskLabel.LOW_RISK_LEVEL,
  TaskLabel.MEDIUM_RISK_LEVEL,
  TaskLabel.HIGH_RISK_LEVEL,
  TaskLabel.CRITICAL_RISK_LEVEL,
  TaskLabel.INDIVIDUAL_TASK,
]);

/**
 * Get user-facing display tags for a task.
 * Filters out metadata labels, deduplicates OS labels
 * (picks the one matching current OS, or drops all OS labels),
 * and caps at MAX_DISPLAY_TAGS.
 */
export function getDisplayTags(
  labels: TaskLabel[],
  currentOS?: TaskLabel | null,
): TaskLabel[] {
  let tags = labels.filter((l) => !HIDDEN_LABELS.has(l));

  // Deduplicate OS labels: keep only current OS, or drop all if no match
  const osTagCount = tags.filter((l) => OS_LABELS.has(l)).length;
  if (osTagCount > 1) {
    if (currentOS && tags.includes(currentOS)) {
      tags = tags.filter((l) => !OS_LABELS.has(l) || l === currentOS);
    } else {
      tags = tags.filter((l) => !OS_LABELS.has(l));
    }
  }

  // Sort: OS-related labels first, then everything else
  const osTags = tags.filter((l) => OS_LABELS.has(l));
  const rest = tags.filter((l) => !OS_LABELS.has(l));

  return [...osTags, ...rest].slice(0, MAX_DISPLAY_TAGS);
}

/**
 * The device a task belongs to, preferring the backup device's own name over
 * the inventory title. Null for user tasks and for devices with neither name.
 */
export function taskDeviceName(task: Task): string | null {
  if (!isDeviceTask(task)) return null;
  return task.backupDeviceName ?? task.deviceCustomTitle;
}

/**
 * The device a task belongs to, or null for user tasks and hidden devices.
 * Use this to match tasks to a device, so a re-registered device isn't
 * confused with its hidden predecessor.
 */
export function taskVisibleDeviceInventoryId(task: Task): string | null {
  return isDeviceTask(task) && !task.deviceHidden
    ? task.deviceInventoryId
    : null;
}

/** The inventory id of the device a task belongs to, null for user tasks. */
export function taskDeviceInventoryId(task: Task): string | null {
  return isDeviceTask(task) ? task.deviceInventoryId : null;
}

/** The OS of the device a task belongs to, null for user tasks. */
export function taskDeviceOs(task: Task): DeviceTaskOperatingSystem | null {
  return isDeviceTask(task) ? task.deviceOs : null;
}

/** Whether the given tasks span more than one visible device. */
export function hasMultipleTaskDevices(tasks: readonly Task[]): boolean {
  const ids = new Set<string>();
  for (const task of tasks) {
    const deviceInventoryId = taskVisibleDeviceInventoryId(task);
    if (deviceInventoryId != null) ids.add(deviceInventoryId);
  }
  return ids.size > 1;
}

export interface OrgTaskCounts {
  total: number;
  yourRemaining: number;
  employeeRemaining: number;
}

export function getOrgTaskCounts(
  tasks: readonly Task[],
  viewerEmail: string | null,
): OrgTaskCounts {
  let yourRemaining = 0;
  let employeeRemaining = 0;
  for (const task of tasks) {
    if (task.isCompletedOrSkipped) continue;
    if (viewerEmail !== null && task.user === viewerEmail) {
      yourRemaining++;
    } else {
      employeeRemaining++;
    }
  }
  return {
    total: tasks.length,
    yourRemaining,
    employeeRemaining,
  };
}

/** Risk level weights for sorting (higher = more severe). */
const RISK_LEVEL_WEIGHT: Record<string, number> = {
  Info: 0,
  Low: 1,
  Medium: 2,
  High: 3,
  Critical: 4,
};

/** Fix references for the "download app" call-to-action tasks. */
const DOWNLOAD_APP_REFS = new Set([
  "download_controla_agent",
  "keep_controla_agent_up_to_date",
]);

export interface TimelineSortOptions {
  isWeb: boolean;
  /** Exact agent ID or the uniquely matched browser device, when known. */
  currentDeviceInventoryId?: string | null;
}

/**
 * Whether a task targets a device other than the one the user is currently on.
 *
 * - Download-app tasks: only actionable on web when the target matches the
 *   current device. Unknown or ambiguous browser devices must not be promoted.
 * - Other tasks on web: any device-specific task is for another device since
 *   the web client is not itself a device.
 * - Other tasks on agent: device-specific tasks whose device
 *   doesn't match the current device.
 */
function isForOtherDevice(task: Task, opts: TimelineSortOptions): boolean {
  // These user-level tasks need another device even though it isn't registered yet.
  if (isDeviceOnboardingTask(task.id)) return true;
  if (DOWNLOAD_APP_REFS.has(task.fixReference)) {
    return (
      !opts.isWeb ||
      opts.currentDeviceInventoryId == null ||
      taskVisibleDeviceInventoryId(task) !== opts.currentDeviceInventoryId
    );
  }
  if (!task.deviceSpecific) return false;
  if (opts.isWeb) return true;
  const deviceInventoryId = taskVisibleDeviceInventoryId(task);
  if (opts.currentDeviceInventoryId == null || deviceInventoryId == null) {
    return false;
  }
  return deviceInventoryId !== opts.currentDeviceInventoryId;
}

/**
 * Priority bucket (lower = shown earlier):
 *
 * 1. Automatable tasks doable on current platform (web/app)
 * 2. Download-app task for the current device on web
 * 3. The checklist (as it will filter other tasks)
 * 4. Other tasks doable on current platform (manual, non-device-specific,
 *    or for the current device on agent)
 * 5. Download-app task for another or unidentified device
 * 6. Automatable tasks for other devices
 * 7. Remaining tasks for other devices
 */
function taskPriorityBucket(task: Task, opts: TimelineSortOptions): number {
  const isDownloadApp = DOWNLOAD_APP_REFS.has(task.fixReference);
  const isTheChecklist = task.id === TaskId.WIZARD_THE_CHECKLIST;
  const status = task.automationStatus.status;
  const isAutomatable =
    status === TaskAutomationStatus.AUTOMATABLE_BACKEND ||
    status === TaskAutomationStatus.AUTOMATABLE_AGENT;

  if (!isForOtherDevice(task, opts)) {
    if (isAutomatable) return 1;
    if (isDownloadApp) return 2;
    if (isTheChecklist) return 3;
    return 4;
  }

  if (isDownloadApp) return 5;
  if (isAutomatable) return 6;
  return 7;
}

/**
 * Sort tasks for the timeline view using `taskPriorityBucket`.
 *
 * Tiebreakers within a bucket: `estimatedDuration` asc, then `riskLevel` desc.
 */
export function sortTimelineTasks(
  tasks: Task[],
  opts: TimelineSortOptions,
): Task[] {
  return [...tasks].sort((a, b) => {
    const bucketDiff =
      taskPriorityBucket(a, opts) - taskPriorityBucket(b, opts);
    if (bucketDiff !== 0) return bucketDiff;

    const durDiff = a.estimatedDuration - b.estimatedDuration;
    if (durDiff !== 0) return durDiff;

    const riskA = RISK_LEVEL_WEIGHT[a.riskLevel] ?? 0;
    const riskB = RISK_LEVEL_WEIGHT[b.riskLevel] ?? 0;
    return riskB - riskA;
  });
}
